// src/pages/Utilisateurs/PrepaPhysique/PrepaPhysique.jsx

import React from "react";
import { FaDumbbell } from "react-icons/fa";
import { ToastContainer } from "react-toastify";

import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";

import useScrollToTop from "../../../hooks/useScollToTop";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import ScrollIndicator from "../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import Reveal from "./../../../components/Reveal/Reveal";

import usePrepaPhysiqueController from "./hooks/usePrepaPhysiqueController";

import PrepaPhysiqueHero from "./components/PrepaPhysiqueHero";
import PrepaPhysiqueToolbar from "./components/PrepaPhysiqueToolbar";
import PrepaPhysiqueAdherentCard from "./components/PrepaPhysiqueAdherentCard";
import SkeletonPrepaCard from "./components/SkeletonPrepaCard";
import EmptyPrepaState from "./components/EmptyPrepaState";

const PrepaPhysique = () => {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const {
    loading,
    adherents,
    filteredAdherents,
    stats,
    ui,
    searchInput,
    setSearchInput,
    dynamicOptions,
    updateUi,
    resetFilters,
    savingKeys,
    toggleExerciseDone,
  } = usePrepaPhysiqueController();

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-slate-50">
      <ScrollIndicator />

      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Utilisateur"
          title="Préparation physique"
          subtitle="Consulte tes exercices, visualise les consignes et coche les séances réalisées."
          icon={FaDumbbell}
          align="left"
          className="mb-4"
        />

        <div className="mb-4">
          <DashboardNavigation role={userRole} />
        </div>

        <Reveal>
          <PrepaPhysiqueHero
            adherentsCount={adherents.length}
            totalExercises={stats.total}
            doneExercises={stats.done}
            todoExercises={stats.todo}
            progress={stats.progress}
          />
        </Reveal>

        <div className="sticky top-[76px] z-30 -mx-3 mt-4 border-y border-slate-200/80 bg-slate-50/95 px-3 py-3 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/90 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-0">
          <PrepaPhysiqueToolbar
            ui={ui}
            dynamicOptions={dynamicOptions}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            onChange={updateUi}
            onReset={resetFilters}
            filteredCount={filteredAdherents.length}
            totalExercises={stats.total}
          />
        </div>

        <section className="mt-5">
          {loading && (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <SkeletonPrepaCard key={index} />
              ))}
            </div>
          )}

          {!loading && adherents.length === 0 && (
            <EmptyPrepaState
              title="Aucun adhérent rattaché"
              description="Aucun adhérent n’est actuellement associé à votre compte."
              onReset={resetFilters}
              showReset={false}
            />
          )}

          {!loading && adherents.length > 0 && filteredAdherents.length === 0 && (
            <EmptyPrepaState
              title="Aucun exercice trouvé"
              description="Aucun exercice ne correspond à ta recherche ou aux filtres sélectionnés."
              onReset={resetFilters}
            />
          )}

          {!loading && filteredAdherents.length > 0 && (
            <div className="space-y-4">
              {filteredAdherents.map((item) => (
                <PrepaPhysiqueAdherentCard
                  key={item.adherent.id}
                  adherent={item.adherent}
                  exercices={item.exercices}
                  totalBeforeFilter={item.totalBeforeFilter}
                  savingKeys={savingKeys}
                  onToggleExercise={toggleExerciseDone}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      <ToastContainer />
    </section>
  );
};

export default PrepaPhysique;